import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import correctMerge from 'lodash.merge';
import { getConfigApiBasePath } from './paths';
import { mergeCustomizer } from '../helpers/util';

/**
 * Merge retrieved config with overrides from loader config
 * @param {object} config - config object retrieved from CDN or config-api
 * @param {object} overrides - config overrides from loader config
 */
function mergeConfig(config, overrides) {
    config = JSON.parse(config);
    config = correctMerge({}, config, overrides, mergeCustomizer);
    return config;
}

/**
 * Retrieve the application configuration from config API
 * @param  {*} configuration - Contains all options for the configuration
 * @param  {*} apiGatewayToken - Used for authentication with the config-api
 * @returns {Promise} - Resolves with the retrieved configuration
 */
function getConfigFromConfigApi(configuration, apiGatewayToken) {
    return new Promise((resolve, reject) => {
        const url = [
            getConfigApiBasePath(configuration.source.environment, configuration.source.version, configuration.source.region),
            configuration.namespace,
            configuration.environment,
        ].join('/');

        if (!apiGatewayToken) {
            throw new Error('The configuration source "config-api" cannot be used without providing a JWT token.');
        }

        const apiSettings = {
            url,
            method: 'get',
            headers: {
                authorization: (apiGatewayToken.indexOf('Bearer ') === 0) ? apiGatewayToken : `Bearer ${apiGatewayToken}`,
            },
            transformResponse: [function transformResponse(data) {
                return mergeConfig(data, configuration.overrides);
            }],
        };

        axios(apiSettings).then((res) => {
            resolve(res.data);
        }).catch(() => {
            reject(configuration);
        });
    });
}

/**
 * Returns the basePath URL for the specified environment
 * @param {string} environment - e.g. prod
 * @returns {string} - BasePath URL
 */
function getBasePath(environment) {
    const basePaths = {
        dev: '//localhost',
        qa: '//mwc-cdn-qa.morningstar.com',
        uat: '//mwc-cdn-uat.morningstar.com',
        prod: '//mwc-cdn.morningstar.com',
    };
    const basePath = basePaths[environment];
    if (!basePath) {
        throw new Error(`Specified configuration environment '${environment}' is not supported`);
    }
    return basePath;
}

/**
 * Returns config URL for the specified params
 * @param {string} environment - e.g. prod
 * @param {string} configurationNamespace - namespace, e.g. client.retail.uk
 * @param {string} source - the source options
 * @returns {string} - configuration URL
 */
function getConfigurationUrl(environment, configurationNamespace, source) {
    let url;
    if (source.type === 'url') {
        // eslint-disable-next-line prefer-destructuring
        url = source.url;
    } else {
        url = [
            getBasePath(environment),
            'components/configurations',
            configurationNamespace.replace(new RegExp('\\.', 'g'), '/'),
            'config.json',
        ].join('/');
    }
    return url;
}

/**
 * Retrieves the application configuration
 * @param  {*} configuration
 * @returns {Promise} - Resolves with the retrieved configuration
 */
function getConfigFromCdn(configuration) {
    return new Promise((resolve, reject) => {
        if (isEmpty(configuration.namespace) && !(configuration.source && configuration.source.type === 'url')) {
            resolve(configuration);
        } else {
            const url = getConfigurationUrl(configuration.environment, configuration.namespace, configuration.source);
            if (url) {
                axios({
                    method: 'get',
                    url,
                    transformResponse: [function transformResponse(data) {
                        return mergeConfig(data, configuration.overrides);
                    }],
                }).then((res) => {
                    resolve(res.data);
                }).catch(() => {
                    reject(configuration);
                });
            } else {
                resolve(configuration);
            }
        }
    });
}

/**
 * @param  {*} configuration - all options for the configuration
 * @param  {*} apiGatewayToken - used for authentication with the config-api
 * @returns {Promise} - Resolves with the retrieved configuration
 */
function getConfiguration(configuration, apiGatewayToken) {
    return new Promise((resolve, reject) => {
        if (isEmpty(configuration) || isEmpty(configuration.source) || isEmpty(configuration.source.type)) {
            resolve(configuration);
        } else if (configuration.source.type === 'none') {
            resolve(configuration.overrides);
        } else if (configuration.source.type === 'config-api') {
            resolve(getConfigFromConfigApi(configuration, apiGatewayToken));
        } else if (configuration.source.type === 'mwc-cdn' || configuration.source.type === 'url') {
            resolve(getConfigFromCdn(configuration));
        } else {
            reject(new Error(`Specified configuration source type '${configuration.source.type}' is not supported`));
        }
    });
}

export default getConfiguration;



// WEBPACK FOOTER //
// ../loader-base/src/services/get-configuration.js